import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchEntityAttachments, deleteAttachment } from "../services/attachmentService";
import { Attachment } from "../types/vehicle";

export type EntityType =
  | "note"
  | "recon_request"
  | "quote"
  | "cr"
  | "buyers_guide"
  | "auction_car_picture"
  | "smog_certificate"
  | "title_for_trade_in"
  | "inspection"
  | "qc_sheet"
  | "loaner_agreement";

export const useEntityAttachments = (
  entityType: EntityType,
  entityId: string | undefined
) => {
  return useQuery<Attachment[]>({
    queryKey: ["entity-attachments", entityType, entityId],
    queryFn: async () => {
      if (!entityId) throw new Error("Entity ID is required");
      const attachments = await fetchEntityAttachments(entityType, entityId);
      return (attachments || []) as Attachment[];
    },
    enabled: !!entityId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useDeleteEntityAttachment = (entityType?: EntityType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const result = await deleteAttachment(attachmentId);
      if (!result.success) {
        throw new Error(result.error || "Failed to delete attachment");
      }
      return result;
    },
    onSuccess: () => {
      // Invalidate all entity attachment queries
      queryClient.invalidateQueries({ queryKey: ["entity-attachments"] });

      // Invalidate specific entity type queries if provided
      if (entityType) {
        queryClient.invalidateQueries({ queryKey: ["entity-attachments", entityType] });
      }

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });

      // Invalidate vehicle history - use predicate to match all vehicle history queries
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "vehicle-history"
      });
    },
  });
};
